import React from 'react'
import { useHistory } from 'react-router-dom'

import useContainerContext from '../../containerContext'
import { useBreakpoint } from '../../../hooks'
import { formatToLocalTime } from '../../../utils/dates'
import Table, { Column } from '../../table'
import { SortableColumn } from '../../table-plugins/sortable'
import AvatarName from '../../avatarName'

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : null
}

const getWorkflowBadgeStyle = (stage) => {
  if (!stage || !stage.color) return null
  const rgb = hexToRgb(stage.color)
  if (!rgb) return null
  return {
    backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`,
    color: stage.color,
  }
}

const getWorkflowBadgeClass = (stage) => {
  if (!stage || !stage.name) return 'default'
  const name = stage.name.toLowerCase()
  if (name === 'added') return 'added'
  if (name === 'scheduled') return 'scheduled'
  if (name.includes('dropped') || name.includes('drop')) return 'dropped-off'
  if (name === 'complete' || name === 'completed') return 'complete'
  return 'default'
}

const sourceLabels = {
  manual: 'Internal',
  adasThink: 'adasThink',
  'client-portal': 'Client',
  'client-portal-upload': 'Client',
}

const getVehicleImageUrl = (image) => {
  if (!image) return null
  if (image.startsWith('http://') || image.startsWith('https://')) return image
  return `https://mobiletechassets.s3.us-east-2.amazonaws.com/colorCropped/${image.split('/').pop()}`
}

const ReportTable = () => {
  const { data = [], state, setState, pricingByVendor = {} } = useContainerContext()
  const { BREAKPOINT, screenBiggerThan } = useBreakpoint()
  const isBiggerThanMD = screenBiggerThan(BREAKPOINT.MD)
  const history = useHistory()

  const canViewPrice = (report) => {
    const vendorId = report.company && (report.company._id || report.company)
    if (!vendorId) return false
    return !!pricingByVendor[vendorId.toString()]
  }

  if (!data.length) {
    return <p className='adas-empty'>No reports found.</p>
  }

  const goToReport = (report) => history.push('/adas-reports/' + report._id)

  // Mobile: card-based layout
  if (!isBiggerThanMD) {
    return (
      <div>
        {data.map(report => {
          const v = report.vehicle || {}
          const vehicle = [v.year, v.make, v.model].filter(Boolean).join(' ') || '—'
          const vendorName = (report.company && report.company.name) || '—'
          const isNew = report.clientPortal && !report.clientPortal.viewedByClient
          const stage = report.workflowStage

          return (
            <div key={report._id} className='adas-mobile-card' onClick={() => goToReport(report)}>
              <div className='d-flex justify-content-between align-items-center'>
                <div className='d-flex align-items-center gap-2'>
                  {isNew && <span className='unread-badge' />}
                  <AvatarName size='small' value={vendorName} className='me-2' />
                  <strong style={{ fontSize: 14, color: '#2A2A2A' }}>{vendorName}</strong>
                </div>
                <small style={{ color: '#AAAAAA' }}>{formatToLocalTime(report.dateCreated)}</small>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: '#2A2A2A' }}>{vehicle}</div>
              <div className='d-flex justify-content-between align-items-center mt-2'>
                <span style={{ fontSize: 14 }}>
                  {canViewPrice(report) && report.totalPrice != null
                    ? `$${Number(report.totalPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : '—'}
                </span>
                {stage && stage.name && (
                  <span className='workflow-badge' style={getWorkflowBadgeStyle(stage) || undefined}>{stage.name}</span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // Desktop: shared Table component (matches estimates/invoices pattern)
  return (
    <Table
      data={data}
      rowKey='_id'
      state={state}
      setState={setState}
      className='primary'
      borderless
      onRowClick={goToReport}
    >
      <Column
        dataKey='company.name'
        width='20%'
        header={<u>Vendor</u>}
        render={(value, { rowData }) => {
          const vendorName = value || '—'
          const isNew = rowData.clientPortal && !rowData.clientPortal.viewedByClient
          return (
            <div className='d-flex align-items-center'>
              {isNew && <span className='unread-badge me-2' />}
              <AvatarName size='medium' value={vendorName} className='me-2' />
              <div>{vendorName}</div>
            </div>
          )
        }}
      />
      <SortableColumn
        dataKey='dateCreated'
        width='8%'
        headerLabel={<u>Date</u>}
        render={value => formatToLocalTime(value)}
      />
      <Column
        dataKey='insuranceCarrier'
        width='8%'
        header={<u>Insurance</u>}
        render={value => value || '—'}
      />
      <Column
        dataKey='source'
        width='8%'
        header={<u>Source</u>}
        render={value => sourceLabels[value] || value || '—'}
      />
      <Column
        dataKey='vehicle'
        width='20%'
        header={<u>Vehicle Info</u>}
        render={(v) => {
          if (!v) return '—'
          const imageUrl = getVehicleImageUrl(v.image)
          return (
            <div className='d-flex align-items-center gap-2'>
              {imageUrl ? (
                <img src={imageUrl} alt='' style={{ width: 88, height: 66, borderRadius: 8, objectFit: 'cover' }} />
              ) : (
                <div style={{ width: 88, height: 66, borderRadius: 8, background: '#EAEAEA' }} />
              )}
              <div style={{ fontSize: 12, lineHeight: '16px', width: '100%' }}>
                <div>
                  <span>{v.year}</span>{' '}
                  <span style={{ fontWeight: 600 }}>{v.make} {v.model}</span>{' '}
                  {v.color && <span style={{ color: '#5D696F' }}>{v.color}</span>}
                </div>
                <div><span style={{ fontWeight: 600 }}>STOCK/RO:</span> {v.stock || 'Not listed'}</div>
                <div><span style={{ fontWeight: 600 }}>VIN:</span> {v.vin || 'Not listed'}</div>
              </div>
            </div>
          )
        }}
      />
      <SortableColumn
        dataKey='totalPrice'
        width='7%'
        headerLabel={<u>Total</u>}
        render={(value, { rowData }) => {
          if (!canViewPrice(rowData)) return '—'
          if (!value && value !== 0) return '—'
          return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        }}
      />
      <Column
        dataKey='clientPortal.calibrationReady'
        width='9%'
        header={<u>Calibration Status</u>}
        render={(value, { rowData }) => {
          const calDate = rowData.clientPortal && rowData.clientPortal.calibrationReadyDate
          if (value === 'yes') return <div><div>READY</div>{calDate && <div style={{ fontSize: 12, color: '#5D696F' }}>{formatToLocalTime(calDate)}</div>}</div>
          if (value === 'no') return 'NOT READY'
          return '—'
        }}
      />
      <Column
        dataKey='workflowStage'
        width='9%'
        header={<u>Workflow Status</u>}
        className='text-center'
        render={(stage) => {
          if (!stage || !stage.name) return '—'
          const dynamicStyle = getWorkflowBadgeStyle(stage)
          return (
            <span
              className={`workflow-badge${!dynamicStyle ? ' ' + getWorkflowBadgeClass(stage) : ''}`}
              style={dynamicStyle || undefined}
            >
              {stage.name}
            </span>
          )
        }}
      />
    </Table>
  )
}

export default ReportTable
